import { useCallback, useEffect, useState } from 'react'

import fetchAllProducts from '../apis/fetchAllProducts'
import { Product } from '../models/product'

const useFarmerProducts = (
  cropId: string,
  categoryId?: string,
  search?: string,
  cache?: Record<string, Product[]>,
) => {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingNext, setLoadingNext] = useState(false)
  const [page, setPage] = useState(1)
  const [hasNext, setHasNext] = useState(false)

  const fetchNext = async () => {
    if (hasNext && !loadingNext) {
      try {
        setLoadingNext(true)
        const response = await fetchAllProducts({
          category_id: categoryId,
          crop_id: cropId,
          search,
          page,
        })
        if (response) {
          setProducts(prev => prev.concat(response.results))
          setPage(prev => prev + 1)
          setHasNext(response.next !== null)
        }
      } catch (e) {
        console.log('Error fetching next page data', e)
      } finally {
        setLoadingNext(false)
      }
    }
  }

  const getProducts = useCallback(async () => {
    if (categoryId) {
      const cacheKey = `${categoryId}-${search}`
      if (cache && cache[cacheKey]) {
        setProducts(cache[cacheKey])
      } else {
        setLoading(true)
        try {
          const response = await fetchAllProducts({
            category_id: categoryId,
            crop_id: cropId,
            search,
            page: 1,
          })
          if (response) {
            setProducts(response.results)
            setPage(prev => prev + 1)
            setHasNext(!!response.next)
            if (cache) cache[cacheKey] = response.results
          }
        } catch (e) {
          console.log('Error in fetching orders', e)
        } finally {
          setLoading(false)
        }
      }
    }
  }, [categoryId, cropId, search])

  useEffect(() => {
    getProducts()
  }, [getProducts])

  return {
    fetchNext,
    products,
    loading,
    loadingNext,
  }
}

export default useFarmerProducts
